import {useCallback, useEffect, useState} from 'react'
import axios from 'axios'
import {toast} from 'sonner'
import {PageTitle} from '../../../_metronic/layout/core'
import {useAuth} from '../../modules/auth'
import {config} from '../../config'
import {convertUtcToLocal} from '../../Helpers'

// All vendors DIIBS currently supports
const POS_VENDORS = ['square', 'clover'] as const
type PosVendor = (typeof POS_VENDORS)[number]

// ─── Types ──────────────────────────────────────────────────────────────────

type PosCheckStatus = 'OPEN' | 'PAID' | 'CANCELLED' | string

interface PosCheck {
  id: string
  bookingId: string
  restaurantId: string
  posVendor: string
  posOrderId: string
  posLocationId?: string
  posStatus: PosCheckStatus
  subtotalAmount?: number        // cents: pre-tax/tip total
  totalAmount?: number           // cents: subtotal + tax + tip
  taxAmount?: number             // cents
  tipAmount?: number             // cents
  currency?: string
  customerFirstName?: string | null
  customerLastName?: string | null
  openedAt?: string | null
  closedAt?: string | null
  businessDate?: string | null
  paymentMethod?: string | null
  fnbSubtotalCents?: number | null  // Clover only
  fnbItemCount?: number | null      // Clover only
  ticketName?: string | null
  rawPayloadS3Key?: string | null
  auctionWinningBidCents?: number | null  // from Booking.amount × 100 (API join)
  lastSyncedAt?: string
  created_at: string
  updated_at: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatMoney(cents?: number, currency = 'USD') {
  if (cents == null) return '—'
  return new Intl.NumberFormat('en-US', {style: 'currency', currency}).format(cents / 100)
}

function StatusBadge({status}: {status: PosCheckStatus}) {
  const map: Record<string, string> = {
    OPEN:      'badge-light-warning',
    PAID:      'badge-light-success',
    CANCELLED: 'badge-light-danger',
  }
  const cls = map[status] ?? 'badge-light-secondary'
  return <span className={`badge ${cls} fw-bold fs-8`}>{status}</span>
}

function VendorBadge({vendor}: {vendor: string}) {
  const map: Record<string, string> = {
    square: 'badge-light-info',
    clover: 'badge-light-success',
  }
  const cls = map[vendor] ?? 'badge-light-secondary'
  return (
    <span className={`badge ${cls} fw-bold fs-8 text-capitalize`}>{vendor}</span>
  )
}

function PaymentBadge({method}: {method?: string | null}) {
  if (!method) return <span className='text-muted fs-8'>—</span>
  const normalized = method.toUpperCase()
  const map: Record<string, string> = {
    CARD: 'badge-light-primary',
    CREDIT_CARD: 'badge-light-primary',
    CASH: 'badge-light-success',
    OTHER: 'badge-light-secondary',
  }
  const cls = map[normalized] ?? 'badge-light-secondary'
  const label = normalized === 'CREDIT_CARD' ? 'CARD' : normalized
  return <span className={`badge ${cls} fw-bold fs-8`}>{label}</span>
}

// ─── Component ───────────────────────────────────────────────────────────────

const PosChecks = () => {
  const {currentUser} = useAuth()
  const BASE_URL = config.apiUrl
  const authHeaders = {Authorization: `Bearer ${currentUser?.api_token}`}

  const [checks, setChecks] = useState<PosCheck[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<Record<string, boolean>>({})
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  // ── Fetch checks from all vendors and merge ─────────────────────────────

  const fetchChecks = useCallback(async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled(
        POS_VENDORS.map(vendor =>
          axios
            .get(`${BASE_URL}/pos/${vendor}/me/checks`, {headers: authHeaders})
            .then(r => (r.data.data?.checks ?? []) as PosCheck[])
            .catch(() => [] as PosCheck[])  // silently ignore vendors that aren't connected
        )
      )

      const merged: PosCheck[] = results
        .flatMap(r => (r.status === 'fulfilled' ? r.value : []))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      setChecks(merged)
      setLastRefreshed(new Date())
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to load POS checks')
    } finally {
      setLoading(false)
    }
  }, [BASE_URL, currentUser?.api_token])

  useEffect(() => {
    fetchChecks()
  }, [fetchChecks])

  // ── Sync a single check (route to the right vendor) ─────────────────────

  const handleSync = async (check: PosCheck) => {
    const vendor = check.posVendor as PosVendor
    setSyncing(s => ({...s, [check.bookingId]: true}))
    try {
      const {data} = await axios.post(
        `${BASE_URL}/pos/${vendor}/me/checks/${check.bookingId}/sync`,
        {},
        {headers: authHeaders}
      )
      const updated: PosCheck = data.data?.posCheck
      if (updated) {
        setChecks(prev => prev.map(c => (c.bookingId === check.bookingId ? {...c, ...updated} : c)))
        toast.success(`Check refreshed → ${updated.posStatus}`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? `Sync failed for booking ${check.bookingId}`)
    } finally {
      setSyncing(s => ({...s, [check.bookingId]: false}))
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <PageTitle breadcrumbs={[]}>POS Checks</PageTitle>

      <div className='card shadow-sm'>
        {/* Header */}
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <h3 className='fw-bold m-0'>POS Checks</h3>
          </div>
          <div className='card-toolbar d-flex align-items-center gap-3'>
            {lastRefreshed && (
              <span className='text-muted fs-8'>
                Last refreshed: {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <button
              className='btn btn-sm btn-light-primary'
              onClick={fetchChecks}
              disabled={loading}
            >
              {loading ? (
                <span className='spinner-border spinner-border-sm me-2' role='status' />
              ) : (
                <i className='ki-duotone ki-arrows-circle fs-5 me-1'>
                  <span className='path1' /><span className='path2' />
                </i>
              )}
              Refresh All
            </button>
          </div>
        </div>

        {/* Body */}
        <div className='card-body py-4'>
          {loading && checks.length === 0 ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading…</span>
              </div>
            </div>
          ) : checks.length === 0 ? (
            <div className='text-center py-10 text-muted'>
              <i className='ki-duotone ki-receipt-square fs-3x text-muted mb-4 d-block'>
                <span className='path1' /><span className='path2' />
              </i>
              <p className='fs-6 fw-semibold'>No POS checks yet.</p>
              <p className='fs-7'>
                Checks appear here once a winning bidder's auction is pushed to your connected POS.
                <br />
                Use the <strong>Simulate Win</strong> button in Connect POS to test.
              </p>
            </div>
          ) : (
            <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted bg-light'>
                    <th className='ps-4 min-w-140px rounded-start'>Booking</th>
                    <th className='min-w-90px'>Vendor</th>
                    <th className='min-w-160px'>Guest</th>
                    <th className='min-w-100px'>Auction</th>
                    <th className='min-w-100px'>F&amp;B Spend</th>
                    <th className='min-w-90px'>Payment</th>
                    <th className='min-w-90px'>Status</th>
                    <th className='min-w-100px'>
                      Total
                      <span
                        className='ms-1 text-muted fs-9'
                        title='Subtotal + tax + tip. Hover row for breakdown.'
                      >ⓘ</span>
                    </th>
                    <th className='min-w-130px'>Last Synced</th>
                    <th className='pe-4 min-w-80px rounded-end text-end'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map(check => {
                    const guestName = [check.customerFirstName, check.customerLastName]
                      .filter(Boolean).join(' ') || null
                    const totalTooltip = [
                      check.subtotalAmount != null ? `Subtotal: ${formatMoney(check.subtotalAmount, check.currency ?? 'USD')}` : null,
                      check.taxAmount      != null ? `Tax: ${formatMoney(check.taxAmount, check.currency ?? 'USD')}` : null,
                      check.tipAmount      != null ? `Tip: ${formatMoney(check.tipAmount, check.currency ?? 'USD')}` : null,
                    ].filter(Boolean).join(' · ') || undefined

                    return (
                      <tr key={check.id}>
                        <td className='ps-4'>
                          <div className='d-flex flex-column'>
                            <span
                              className='text-gray-800 fw-bold fs-7 font-monospace'
                              title={check.bookingId}
                            >
                              {check.bookingId.slice(0, 8)}…
                            </span>
                            {check.ticketName && (
                              <span className='text-muted fs-9'>{check.ticketName}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <VendorBadge vendor={check.posVendor} />
                        </td>
                        <td>
                          {guestName
                            ? <span className='text-gray-800 fw-semibold fs-7'>{guestName}</span>
                            : <span className='text-muted fs-8'>—</span>
                          }
                        </td>
                        <td className='fw-semibold text-gray-700 fs-7'>
                          {check.auctionWinningBidCents != null
                            ? formatMoney(check.auctionWinningBidCents, check.currency ?? 'USD')
                            : <span className='text-muted'>—</span>
                          }
                        </td>
                        <td className='text-gray-700 fs-7'>
                          {check.posVendor === 'clover'
                            ? check.fnbSubtotalCents != null
                              ? formatMoney(check.fnbSubtotalCents, check.currency ?? 'USD')
                              : <span className='text-muted'>—</span>
                            : <span className='text-muted fs-9' title='F&B tracked separately on Square'>—</span>
                          }
                        </td>
                        <td>
                          <PaymentBadge method={check.paymentMethod} />
                        </td>
                        <td>
                          <StatusBadge status={check.posStatus} />
                        </td>
                        <td className='fw-bold text-gray-800' title={totalTooltip}>
                          {formatMoney(check.totalAmount, check.currency ?? 'USD')}
                        </td>
                        <td className='text-muted fs-8'>
                          {check.lastSyncedAt ? convertUtcToLocal(check.lastSyncedAt) : '—'}
                        </td>
                        <td className='text-end pe-4'>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            title={`Refresh from ${check.posVendor}`}
                            disabled={!!syncing[check.bookingId]}
                            onClick={() => handleSync(check)}
                          >
                            {syncing[check.bookingId] ? (
                              <span className='spinner-border spinner-border-sm' role='status' />
                            ) : (
                              <i className='ki-duotone ki-arrows-circle fs-4'>
                                <span className='path1' /><span className='path2' />
                              </i>
                            )}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer summary */}
        {checks.length > 0 && (
          <div className='card-footer border-0 pt-0'>
            <div className='d-flex gap-6 flex-wrap'>
              {(['OPEN', 'PAID', 'CANCELLED'] as PosCheckStatus[]).map(s => {
                const count = checks.filter(c => c.posStatus === s).length
                if (count === 0) return null
                return (
                  <div key={s} className='d-flex align-items-center gap-2'>
                    <StatusBadge status={s} />
                    <span className='text-muted fs-8'>{count}</span>
                  </div>
                )
              })}
              {POS_VENDORS.map(v => {
                const count = checks.filter(c => c.posVendor === v).length
                if (count === 0) return null
                return (
                  <div key={v} className='d-flex align-items-center gap-2'>
                    <VendorBadge vendor={v} />
                    <span className='text-muted fs-8'>{count}</span>
                  </div>
                )
              })}
              <span className='text-muted fs-8 ms-auto'>{checks.length} total checks</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default PosChecks
